import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BellIcon, MailIcon, MessageSquareIcon, SmartphoneIcon, XIcon } from 'lucide-react';
import { materials, type MaterialId } from '../../data/collectes';
const leadTimes = ['1 heure à l’avance', '2 heures à l’avance', 'La veille au soir', 'Le matin même'];
const channels: {
  id: string;
  label: string;
  Icon: typeof MailIcon;
}[] = [{
  id: 'courriel',
  label: 'Courriel',
  Icon: MailIcon
}, {
  id: 'sms',
  label: 'Message texte',
  Icon: MessageSquareIcon
}, {
  id: 'push',
  label: 'Notification mobile',
  Icon: SmartphoneIcon
}];
const dots: Record<MaterialId, string> = {
  ordures: 'bg-[#475569]',
  recyclage: 'bg-[#0f766e]',
  organiques: 'bg-[#3f6212]',
  verts: 'bg-[#a16207]'
};
const materialIds = Object.keys(materials) as MaterialId[];
interface ReminderPreferencesProps {
  onClose: () => void;
}
export function ReminderPreferences({
  onClose
}: ReminderPreferencesProps) {
  const [leadTime, setLeadTime] = useState(leadTimes[0]);
  const [activeChannels, setActiveChannels] = useState<string[]>(['courriel']);
  const [activeMaterials, setActiveMaterials] = useState<MaterialId[]>(['ordures']);
  const toggle = <T,>(list: T[], value: T): T[] => list.includes(value) ? list.filter(item => item !== value) : [...list, value];
  return <motion.section aria-labelledby="rappels-titre" initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="order-first mb-6 rounded-3xl border border-slate-200 bg-white p-6 lg:order-none lg:mb-0 lg:mt-6" data-id="element-803" data-mp="35kmc8ki7srb8">
      <div className="flex items-start justify-between gap-4" data-id="element-804" data-mp="3shnw1r2s54mr">
        <div data-id="element-805" data-mp="3ohng22018ldl">
          <h3 id="rappels-titre" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-806" data-mp="3oi6rczv6gbin">
            <BellIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-807" data-mp="k3yrmhgi3yh2" />
            Rappels de collecte
          </h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-808" data-mp="2ecksr0iily8n">Choisissez quand et comment vous voulez être averti.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fermer les préférences de rappel" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-809" data-mp="2jd8f38vstqwj">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-810" data-mp="20m8v6m35v7a4" />
        </button>
      </div>

      <div className="mt-5" data-id="element-811" data-mp="1loow1vsy46bv">
        <label htmlFor="rappel-delai" className="text-sm font-bold text-ville-navy" data-id="element-812" data-mp="1lphzamsp4dp9">
          Envoyer mon rappel
        </label>
        <select id="rappel-delai" value={leadTime} onChange={event => setLeadTime(event.target.value)} className="mt-2 w-full max-w-xs rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-813" data-mp="3g1oavmpi4wi3">
          {leadTimes.map(option => <option key={option} value={option} data-id="element-814" data-mp="1j5230kvke3zu">
              {option}
            </option>)}
        </select>
      </div>

      <fieldset className="mt-6" data-id="element-815" data-mp="1s3dsgjkp2vqp">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-816" data-mp="1s2cbxzkwth1l">Comment vous joindre ?</legend>
        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3" data-id="element-817" data-mp="1o10nhxdrv1uz">
          {channels.map(({
          id,
          label,
          Icon
        }) => <label key={id} className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ville-navy" data-id="element-818" data-mp="221bed0f30gg5">
              <input type="checkbox" checked={activeChannels.includes(id)} onChange={() => setActiveChannels(current => toggle(current, id))} className="h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-819" data-mp="1x0nq2mi8mdpm" />
              <Icon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-820" data-mp="488z4voinwv1" />
              {label}
            </label>)}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-821" data-mp="e5dhslj5la73">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-822" data-mp="e27wlhdnasgn">Pour quelles collectes voulez-vous un rappel ?</legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-823" data-mp="3hl14pennmp3f">
          {materialIds.map(id => <label key={id} className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ville-navy" data-id="element-824" data-mp="3mjm6vjmgviui">
              <input type="checkbox" checked={activeMaterials.includes(id)} onChange={() => setActiveMaterials(current => toggle(current, id))} className="h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-825" data-mp="3rip086t340mn" />
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dots[id]}`} aria-hidden="true" data-id="element-826" data-mp="33sclod3c35n3" />
              {materials[id].label}
            </label>)}
        </div>
      </fieldset>

      <div className="mt-6 flex items-center justify-end gap-3" data-id="element-827" data-mp="1gxpt6gagud63">
        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-828" data-mp="1bxbjktt7uv5y">
          Annuler
        </button>
        <button type="button" onClick={onClose} className="rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-829" data-mp="x7mg2rxnif2i">
          Enregistrer les rappels
        </button>
      </div>
    </motion.section>;
}